// Context + Provider for the multi-step login form.
// Mirrors frontend/src/app/login/_providers/login-flow-provider.tsx without Next.js deps.
import {
  useMemo,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@/lib/query";
import type { LoginFlowReturn, LoginFlowState } from "../types";
import {
  createInitialLoginFlowState,
  loginFlowReducer,
  isValidEmail,
  SUBTITLES,
  sanitizeUrl,
} from "./LoginFormReducer";
import { LoginFormContext } from "./LoginFormCtx";
import { type ApiErrorShape, parseErrorPayload } from "./loginFormErrors";
import {
  isAuthRecord as isRecord,
  parseIdentifyPayload,
  postAuthJson as apiPost,
  type ApiPostResult,
} from "./loginFormApi";
import { validateReturnTo } from "@/lib/catalog-pending/returnto-validator";

const LICENSE_BLOCK_CODES = new Set([
  "LICENSE_ACCESS_DENIED",
  "TEMPORARILY_BLOCKED",
  "CANCELLED",
]);
const RATE_LIMIT_CODE = "RATE_LIMITED";
const RATE_LIMIT_ERROR = "rate_limited";
const RATE_LIMIT_FALLBACK_SECONDS = 60;

// Reads a returnTo hint from BOTH router state.from (set by <RequireAuth>) and
// the ?from=… query (set by deep links). Always runs the candidate through
// validateReturnTo so loops to /login // /post-login and external/protocol-
// relative targets fall back to "/" (#46 α-A12 / C46-DEEP-LINK-LOOP-REJECT).
function readRedirectPath(state: unknown, search: string): string | null {
  const stateFrom =
    isRecord(state) && typeof state.from === "string" ? state.from : null;
  const queryFrom = new URLSearchParams(search).get("from");
  const candidate = stateFrom ?? queryFrom;
  if (candidate === null || candidate === "") return null;
  const safe = validateReturnTo(candidate, {
    currentOrigin: window.location.origin,
  });
  return safe === "/" ? null : safe;
}

const CHECKOUT_LOGIN_STATES = new Set([
  "none",
  "expired",
  "canceled_within",
  "canceled_expired",
]);

function readLicenseState(raw: unknown): string | null {
  const root =
    isRecord(raw) && isRecord(raw.data) ? raw.data : isRecord(raw) ? raw : {};
  const user = isRecord(root.user) ? root.user : {};
  const state = root.licenseState ?? user.licenseState;
  return typeof state === "string" ? state : null;
}

function checkoutRedirectForLogin(email: string, raw: unknown): string | null {
  const licenseState = readLicenseState(raw);
  if (!licenseState || !CHECKOUT_LOGIN_STATES.has(licenseState)) return null;
  const params = new URLSearchParams({ from_login: "1", email });
  return `/comprar?${params.toString()}`;
}

type ApplyPaymentLinkDispatch = React.Dispatch<{
  type: "APPLY_PAYMENT_LINK";
  url: string;
  name: string;
  priceCents: number;
}>;

function extractPaymentLink(
  json: unknown,
): { url: string; name: string; priceCents: number } | null {
  const pl =
    isRecord(json) && isRecord(json.data)
      ? json.data
      : isRecord(json)
        ? json
        : null;
  const link =
    isRecord(pl) && isRecord(pl.paymentLink)
      ? pl.paymentLink
      : isRecord(pl)
        ? pl
        : null;
  if (!link) return null;
  const url = sanitizeUrl(typeof link.url === "string" ? link.url : "");
  if (!url) return null;
  return {
    url,
    name: typeof link.name === "string" ? link.name : "",
    priceCents: typeof link.priceCents === "number" ? link.priceCents : 0,
  };
}

function usePaymentLinkEffect(dispatch: ApplyPaymentLinkDispatch): void {
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    fetch("/api/v2/auth/payment-link", {
      credentials: "include",
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((json: unknown) => {
        if (!alive) return;
        const link = extractPaymentLink(json);
        if (link) dispatch({ type: "APPLY_PAYMENT_LINK", ...link });
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [dispatch]); // dispatch from useReducer is stable — only listed to satisfy exhaustive-deps
}

type FlowDispatch = React.Dispatch<Parameters<typeof loginFlowReducer>[1]>;

function dispatchIdentifyError(
  err: ApiErrorShape,
  status: number,
  dispatch: FlowDispatch,
): void {
  if (status === 0) {
    dispatch({ type: "SET_ERROR", error: "Erro ao conectar" });
    return;
  }
  const code = err.code;
  if (code && LICENSE_BLOCK_CODES.has(code)) {
    dispatch({
      type: "SET_LICENSE_BLOCKED",
      error: err.message ?? "Acesso bloqueado.",
    });
    return;
  }
  dispatch({ type: "SET_ERROR", error: identifyErrorMessage(err, status) });
}

function identifyErrorMessage(err: ApiErrorShape, status: number): string {
  if (isUnavailableIdentifyError(err, status)) return "Servidor indisponível.";
  return err.message ?? "Erro ao verificar email";
}

function isUnavailableIdentifyError(
  err: ApiErrorShape,
  status: number,
): boolean {
  const emptyServerError = status >= 500 && !err.code && !err.message;
  return (
    emptyServerError ||
    err.code === "MATRIX_UNAVAILABLE" ||
    err.message === "internal_error"
  );
}

function handleIdentifyResponse(
  res: ApiPostResult,
  isCurrent: (id: number) => boolean,
  id: number,
  dispatch: FlowDispatch,
): void {
  if (!isCurrent(id)) return;
  if (res.ok) {
    dispatch({
      type: "APPLY_IDENTIFY",
      result: parseIdentifyPayload(res.data),
    });
    return;
  }
  dispatchIdentifyError(parseErrorPayload(res.data), res.status, dispatch);
}

function buildEmailSubmitHandler(
  email: string,
  startOp: () => number,
  isCurrent: (id: number) => boolean,
  dispatch: FlowDispatch,
): () => Promise<void> {
  return async (): Promise<void> => {
    if (!isValidEmail(email)) {
      dispatch({ type: "SET_ERROR", error: "Digite um email válido" });
      return;
    }
    const id = startOp();
    dispatch({ type: "SET_LOADING", isLoading: true });
    dispatch({ type: "SET_ERROR", error: "" });
    dispatch({ type: "CLEAR_LICENSE_BLOCKED" });
    try {
      const res = await apiPost("/api/v2/auth/identify", { login: email });
      if (!isCurrent(id)) return;
      handleIdentifyResponse(res, isCurrent, id, dispatch);
    } catch {
      if (isCurrent(id))
        dispatch({ type: "SET_ERROR", error: "Erro ao conectar" });
    } finally {
      if (isCurrent(id)) dispatch({ type: "SET_LOADING", isLoading: false });
    }
  };
}

interface LoginHandlerOpts {
  email: string;
  password: string;
  startOp: () => number;
  isCurrent: (id: number) => boolean;
  dispatch: FlowDispatch;
  onSuccess: (data: unknown) => void;
}

function loginErrorMessage(data: unknown): string {
  const err = parseErrorPayload(data);
  const raw = err.code ?? err.message;
  if (raw === "AUTH_TEMPORARILY_BLOCKED" || raw === "TEMPORARILY_BLOCKED") {
    return "Conta temporariamente bloqueada";
  }
  if (raw === "invalid_credentials") {
    return "Senha incorreta. As credenciais são as mesmas da sua conta Matrix.";
  }
  return err.message ?? "Credenciais inválidas";
}

function positiveInteger(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : value;
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  return Math.ceil(n);
}

function payloadRetryAfterSeconds(data: unknown): number | null {
  if (!isRecord(data)) return null;
  return (
    positiveInteger(data.retryAfterSeconds) ??
    positiveInteger(data.retry_after_seconds)
  );
}

function retryAfterSeconds(res: ApiPostResult): number {
  return (
    positiveInteger(res.retryAfterHeader) ??
    payloadRetryAfterSeconds(res.data) ??
    RATE_LIMIT_FALLBACK_SECONDS
  );
}

function hasRateLimitPayload(data: unknown): boolean {
  const err = parseErrorPayload(data);
  if (err.code === RATE_LIMIT_CODE) return true;
  return isRecord(data) && data.error === RATE_LIMIT_ERROR;
}

function isRateLimitedResponse(res: ApiPostResult): boolean {
  return res.status === 429 || hasRateLimitPayload(res.data);
}

function dispatchLoginFailure(
  res: ApiPostResult,
  dispatch: FlowDispatch,
): void {
  if (!isRateLimitedResponse(res)) {
    dispatch({ type: "SET_ERROR", error: loginErrorMessage(res.data) });
    return;
  }
  const seconds = retryAfterSeconds(res);
  dispatch({
    type: "SET_RATE_LIMIT",
    error: loginErrorMessage(res.data),
    untilMs: Date.now() + seconds * 1000,
  });
}

function buildLoginHandler(opts: LoginHandlerOpts): () => Promise<void> {
  const { email, password, startOp, isCurrent, dispatch, onSuccess } = opts;
  return async (): Promise<void> => {
    if (!isValidEmail(email) || !password.trim()) {
      dispatch({ type: "SET_ERROR", error: "Preencha email e senha" });
      return;
    }
    const id = startOp();
    dispatch({ type: "SET_LOADING", isLoading: true });
    dispatch({ type: "SET_ERROR", error: "" });
    try {
      // #46 C46-LEMBRAR-ME-REMOVIDO: no `rememberMe` — single 30-day session.
      const res = await apiPost("/api/v2/auth/login", {
        login: email,
        password,
      });
      if (!isCurrent(id)) return;
      if (res.ok) {
        onSuccess(res.data);
        return;
      }
      dispatchLoginFailure(res, dispatch);
    } catch {
      if (isCurrent(id))
        dispatch({ type: "SET_ERROR", error: "Erro ao conectar" });
    } finally {
      if (isCurrent(id)) dispatch({ type: "SET_LOADING", isLoading: false });
    }
  };
}

function useLoginSubmitHandler(
  state: LoginFlowState,
  dispatch: FlowDispatch,
  startOp: () => number,
  isCurrent: (id: number) => boolean,
): (e: React.FormEvent) => Promise<void> {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  return useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      if (
        state.rateLimitUntilMs !== null &&
        state.rateLimitUntilMs > Date.now()
      ) {
        return;
      }
      const from = readRedirectPath(location.state, location.search);
      const email = state.email.trim();
      const onSuccess = (data: unknown): void => {
        queryClient.clear();
        // #android-s8-login-flow: LayoutScrollRestoration only runs under
        // DashboardLayout, so /robot, /, and /comprar inherit whatever
        // scrollY the document had on /login. Reset before navigate so the
        // operator lands at the top of the next page (operator report
        // 2026-05-28: "depois loga lá embaixo da tela").
        window.scrollTo(0, 0);
        const checkoutRedirect = checkoutRedirectForLogin(email, data);
        if (checkoutRedirect) {
          navigate(checkoutRedirect);
          return;
        }
        navigate(from ?? "/");
      };
      await buildLoginHandler({
        email,
        password: state.password,
        startOp,
        isCurrent,
        dispatch,
        onSuccess,
      })();
    },
    [
      state.email,
      state.password,
      state.rateLimitUntilMs,
      navigate,
      queryClient,
      location.state,
      location.search,
      dispatch,
      startOp,
      isCurrent,
    ],
  );
}

// eslint-disable-next-line max-lines-per-function
export function LoginFormProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [state, dispatch] = useReducer(
    loginFlowReducer,
    undefined,
    createInitialLoginFlowState,
  );
  // Inflight op counter — same pattern as original use-inflight-ops.ts.
  const opSeq = useRef(0);
  const opId = useRef(0);

  usePaymentLinkEffect(dispatch as ApplyPaymentLinkDispatch);

  useEffect(() => {
    if (state.rateLimitUntilMs === null) return;
    const delayMs = Math.max(0, state.rateLimitUntilMs - Date.now()) + 100;
    const timer = window.setTimeout(() => {
      dispatch({ type: "CLEAR_RATE_LIMIT" });
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [state.rateLimitUntilMs]);

  function startOp(): number {
    opSeq.current += 1;
    opId.current = opSeq.current;
    return opId.current;
  }
  function isCurrent(id: number): boolean {
    return opId.current === id;
  }

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      await buildEmailSubmitHandler(
        state.email.trim(),
        startOp,
        isCurrent,
        dispatch,
      )();
    },
    [state.email],
  ); // stable: startOp/isCurrent are local fns, dispatch is from useReducer

  const handleLogin = useLoginSubmitHandler(
    state,
    dispatch,
    startOp,
    isCurrent,
  );
  const handleBack = useCallback((): void => {
    opSeq.current += 1;
    dispatch({ type: "BACK_TO_EMAIL" });
  }, []);

  const handleRetry = useCallback((): void => {
    const email = state.email.trim();
    if (!isValidEmail(email)) return;
    const id = startOp();
    dispatch({ type: "SET_LOADING", isLoading: true });
    apiPost("/api/v2/auth/identify", { login: email })
      .then((res) => handleIdentifyResponse(res, isCurrent, id, dispatch))
      .catch(() => {
        if (isCurrent(id))
          dispatch({ type: "SET_ERROR", error: "Erro ao conectar" });
      })
      .finally(() => {
        if (isCurrent(id)) dispatch({ type: "SET_LOADING", isLoading: false });
      });
  }, [state.email]); // stable: startOp/isCurrent are local fns, dispatch is stable

  const setEmail = useCallback(
    (v: string) => dispatch({ type: "SET_EMAIL", email: v }),
    [],
  );
  const setPassword = useCallback(
    (v: string) => dispatch({ type: "SET_PASSWORD", password: v }),
    [],
  );
  const getSubtitle = useCallback(() => SUBTITLES[state.step], [state.step]);

  const value = useMemo<LoginFlowReturn>(
    () => ({
      ...state,
      setEmail,
      setPassword,
      handleEmailSubmit,
      handleLogin,
      handleBack,
      handleRetry,
      getSubtitle,
    }),
    [
      state,
      setEmail,
      setPassword,
      handleEmailSubmit,
      handleLogin,
      handleBack,
      handleRetry,
      getSubtitle,
    ],
  );

  return (
    <LoginFormContext.Provider value={value}>
      {children}
    </LoginFormContext.Provider>
  );
}

// Hook lives in hooks/useLoginFormContext.ts — imported from there by consumers.
